import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Send, CheckCircle2, XCircle, Upload, RotateCcw, History, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATUS_COLOR = {
  draft:     "bg-zinc-200 text-zinc-800",
  review:    "bg-amber-100 text-amber-800",
  approved:  "bg-emerald-100 text-emerald-800",
  published: "bg-primary text-primary-foreground",
};

// Available workflow actions per status. Mirrors backend TRANSITIONS.
const ACTIONS_BY_STATUS = {
  draft:     [{ id: "submit",  label: "Submit for Review", icon: Send,        variant: "default" }],
  review:    [
    { id: "approve", label: "Approve",       icon: CheckCircle2, variant: "default" },
    { id: "reject",  label: "Send back",     icon: XCircle,      variant: "outline" },
  ],
  approved:  [
    { id: "publish", label: "Publish",       icon: Upload,       variant: "default" },
    { id: "revise",  label: "Revise",        icon: RotateCcw,    variant: "outline" },
  ],
  published: [{ id: "revise",  label: "Start Revision", icon: RotateCcw,    variant: "outline" }],
};

const ACTION_COPY = {
  submit:  { title: "Submit for review",        placeholder: "Optional note for reviewers…" },
  approve: { title: "Approve SOP",              placeholder: "Optional approval note…" },
  reject:  { title: "Send back to draft",       placeholder: "Reason for rejection (recommended)…" },
  publish: { title: "Publish SOP",              placeholder: "Optional release note…" },
  revise:  { title: "Start a new revision",     placeholder: "Reason for revision…" },
};

export default function SOPDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [sop, setSop] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // action id in dialog
  const [note, setNote] = useState("");

  const load = () =>
    api.get(`/sops/${id}`).then((r) => setSop(r.data)).catch(() => {
      toast.error("SOP not found");
      nav("/app/sops");
    });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const actions = useMemo(
    () => (sop ? ACTIONS_BY_STATUS[sop.status] || [] : []),
    [sop],
  );

  const canApprove = user?.role === "owner" || user?.role === "admin";

  const runTransition = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sops/${id}/transition`, {
        action: pending, note,
      });
      setSop(data);
      toast.success(`SOP ${pending}${pending.endsWith("e") ? "d" : "ed"}`);
      setPending(null);
      setNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    await api.delete(`/sops/${id}`);
    toast.success("SOP deleted");
    nav("/app/sops");
  };

  if (!sop) return <div className="p-8 font-mono-plex text-sm">Loading SOP…</div>;

  return (
    <div className="p-8 space-y-6" data-testid="sop-detail-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/app/sops" className="inline-flex items-center gap-1 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-library">
            <ArrowLeft className="w-3 h-3" /> Back to library
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-3" data-testid="sop-title">
            {sop.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Badge
              className={`${STATUS_COLOR[sop.status]} rounded-sm font-mono-plex text-[10px] uppercase tracking-widest`}
              data-testid="sop-status-badge"
            >
              {sop.status}
            </Badge>
            <span className="text-xs font-mono-plex text-muted-foreground uppercase tracking-widest">
              v{sop.version} · {sop.department} · by {sop.author_name}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="sop-actions">
          {actions.map((a) => {
            const Icon = a.icon;
            const disabled = (a.id === "approve" || a.id === "publish") && !canApprove;
            return (
              <Button
                key={a.id}
                variant={a.variant}
                disabled={disabled}
                onClick={() => { setPending(a.id); setNote(""); }}
                data-testid={`sop-action-${a.id}`}
                className={`rounded-sm ${a.variant === "default" ? "bg-primary hover:bg-primary/90" : ""}`}
                title={disabled ? "Requires owner/admin role" : undefined}
              >
                <Icon className="w-4 h-4 mr-2" />
                {a.label}
              </Button>
            );
          })}
          <Button variant="ghost" size="icon" onClick={del} data-testid="sop-delete-btn">
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="rounded-sm" data-testid="sop-transition-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {pending && ACTION_COPY[pending]?.title}
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="tnote">Note</Label>
            <Textarea
              id="tnote" rows={3} className="rounded-sm mt-1.5"
              placeholder={pending ? ACTION_COPY[pending].placeholder : ""}
              value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="sop-transition-note"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline" onClick={() => setPending(null)}
              className="rounded-sm" data-testid="sop-transition-cancel"
            >Cancel</Button>
            <Button
              onClick={runTransition} disabled={busy}
              className="rounded-sm bg-primary hover:bg-primary/90"
              data-testid="sop-transition-confirm"
            >
              {busy ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content + History */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 border border-border bg-card p-8" data-testid="sop-content">
          <div className="overline text-muted-foreground mb-4">Document</div>
          <pre className="whitespace-pre-wrap font-mono-plex text-sm leading-relaxed">
{sop.content || "// No content."}
          </pre>
        </div>

        <aside className="lg:col-span-2 space-y-4">
          <div className="border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-primary" />
              <div className="overline text-muted-foreground">Workflow History</div>
            </div>
            <ol className="space-y-4" data-testid="sop-history">
              {(sop.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="border-l-2 border-border pl-4 relative">
                  <div className="absolute -left-[5px] top-1 w-2 h-2 bg-primary rounded-full" />
                  <div className="overline text-primary">{h.action}</div>
                  <div className="text-sm mt-0.5">
                    {h.from_status ? `${h.from_status} → ` : ""}<span className="font-medium">{h.to_status}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {h.by_name} · {new Date(h.at).toLocaleString()}
                  </div>
                  {h.note && (
                    <div className="text-xs mt-2 text-foreground/80 italic">&ldquo;{h.note}&rdquo;</div>
                  )}
                </li>
              ))}
            </ol>
          </div>

          <div className="border border-border bg-card p-6">
            <div className="overline text-muted-foreground mb-3">Workflow</div>
            <div className="flex items-center gap-1 flex-wrap font-mono-plex text-[10px] uppercase tracking-widest">
              {["draft", "review", "approved", "published"].map((s, i) => (
                <React.Fragment key={s}>
                  <span
                    className={`px-2 py-1 border ${sop.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                    data-testid={`workflow-step-${s}`}
                  >
                    {s}
                  </span>
                  {i < 3 && <span className="text-muted-foreground">→</span>}
                </React.Fragment>
              ))}
            </div>
            {!canApprove && (
              <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                Approvals &amp; publishing require an owner or admin role.
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
